import React, { useState, useCallback, memo, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Link,
  Collapse,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VerifiedIcon from "@mui/icons-material/Verified";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ListIcon from "@mui/icons-material/List";
import styles from "./training-search-agent.module.scss";

type TrainingSearchAgentProps = {
  parsedContent: any;
};

type Course = {
  course_id: string;
  course_name: string;
  provider: string;
  url: string;
  content_mode: string;
  duration_display: string | null;
  duration_hours: number | null;
  cost: number | null;
  certification_available: boolean | null;
  skills_covered: string[];
  skill_match_score: number;
  recommendation_reason: string;
  budget_status: string;
  timeframe_status: string;
  source: string;
  language: string;
  currency: string | null;
  pricing_model: string | null;
};

type SkillRecommendation = {
  skill_name: string;
  priority: number;
  course_ids: string[];
};

type TrainingData = {
  agent_name: string;
  courses: Course[];
  recommendations_by_skill: SkillRecommendation[];
};

const getStatusColor = (status: string): string => {
  const upper = status.toUpperCase();
  if (upper === "WITHIN_BUDGET" || upper === "COMFORTABLE") return "#10b981";
  if (upper === "STRETCH" || upper === "TIGHT") return "#f59e0b";
  if (upper === "OVER_BUDGET" || upper === "EXCEEDS") return "#ef4444";
  return "#9ca3af";
};

const getStatusLabel = (status: string): string => {
  if (status === "UNKNOWN") return "Not Specified";
  return status.replace(/_/g, " ");
};

const formatCost = (cost: number | null, currency: string | null): string => {
  if (cost === null || cost === 0) return "Free";
  return cost.toFixed(2);
};

const getMatchScoreColor = (score: number): string => {
  if (score >= 90) return "#10b981";
  if (score >= 75) return "#f59e0b";
  return "#6b7280";
};

const CourseCard = memo<{
  course: Course;
  isReasonExpanded: boolean;
  onToggleReason: (courseId: string) => void;
}>(({ course, isReasonExpanded, onToggleReason }) => (
  <Card className={styles.courseCard}>
    <CardContent className={styles.courseCardContent}>
      {/* Course Title and Provider */}
      <Box className={styles.courseTitleRow}>
        <Typography className={styles.courseTitle}>
          {course.course_name}
        </Typography>
      </Box>

      <Box className={styles.providerRow}>
        <Typography className={styles.providerText}>
          {course.provider}
        </Typography>
        <Chip
          label={course.content_mode.replace("/", " • ")}
          size="small"
          className={styles.modeChip}
        />
      </Box>

      {/* Match Score Badge */}
      <Box className={styles.matchScoreBox}>
        <Chip
          label={`${course.skill_match_score}% Match`}
          size="small"
          icon={<TrendingUpIcon sx={{ fontSize: 14, color: "#fff" }} />}
          sx={{
            backgroundColor: getMatchScoreColor(course.skill_match_score),
            color: "#fff",
            fontWeight: 700,
            fontSize: "12px",
          }}
        />
      </Box>

      {/* Recommendation Reason - Expandable */}
      {course.recommendation_reason && (
        <>
          <Box
            className={styles.reasonToggle}
            onClick={() => onToggleReason(course.course_id)}
          >
            <Typography className={styles.reasonToggleText}>
              Why this course?
            </Typography>
            {isReasonExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            )}
          </Box>
          <Collapse in={isReasonExpanded} timeout="auto" unmountOnExit>
            <Box className={styles.reasonBox}>
              <Typography className={styles.reasonText}>
                {course.recommendation_reason}
              </Typography>
            </Box>
          </Collapse>
        </>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Duration and Cost */}
      <Box className={styles.detailsRow}>
        {course.duration_display && (
          <Box className={styles.detailItem}>
            <AccessTimeIcon sx={{ fontSize: 16, color: "#6b7280" }} />
            <Typography className={styles.detailText}>
              {course.duration_display}
            </Typography>
          </Box>
        )}
        <Box className={styles.detailItem}>
          <Typography className={styles.detailText}>
            {formatCost(course.cost, course.currency)}
          </Typography>
        </Box>
      </Box>

      {/* Status Badges */}
      <Box className={styles.statusRow}>
        {course.budget_status !== "UNKNOWN" && (
          <Chip
            label={getStatusLabel(course.budget_status)}
            size="small"
            sx={{
              backgroundColor: getStatusColor(course.budget_status),
              color: "#fff",
              fontSize: "11px",
              fontWeight: 600,
            }}
          />
        )}
        {course.timeframe_status !== "UNKNOWN" && (
          <Chip
            label={getStatusLabel(course.timeframe_status)}
            size="small"
            sx={{
              backgroundColor: getStatusColor(course.timeframe_status),
              color: "#fff",
              fontSize: "11px",
              fontWeight: 600,
            }}
          />
        )}
        {course.certification_available && (
          <Chip
            label="Certification"
            size="small"
            icon={<VerifiedIcon sx={{ fontSize: 12 }} />}
            className={styles.certChip}
          />
        )}
      </Box>

      {/* Skills Covered */}
      {course.skills_covered && course.skills_covered.length > 0 && (
        <Box className={styles.skillsCoveredSection}>
          <Typography className={styles.skillsCoveredLabel}>
            Skills covered:
          </Typography>
          <Box className={styles.skillsChipsBox}>
            {course.skills_covered.map((skill, idx) => (
              <Chip
                key={idx}
                label={skill}
                size="small"
                className={styles.skillChip}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* View Course Link */}
      {course.url ? (
        <Link
          href={course.url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.viewCourseLink}
        >
          <span>View Course</span>
          <OpenInNewIcon sx={{ fontSize: 16 }} />
        </Link>
      ) : (
        <Box className={styles.noLinkBox}>
          <Typography className={styles.noLinkText}>
            Contact training coordinator for enrollment
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
));

CourseCard.displayName = "CourseCard";

const SkillSection = memo<{
  skillRec: SkillRecommendation;
  skillKey: string;
  isExpanded: boolean;
  skillCourses: Course[];
  expandedReasons: Set<string>;
  onToggleSkill: (skillKey: string) => void;
  onToggleReason: (courseId: string) => void;
}>(
  ({
    skillRec,
    skillKey,
    isExpanded,
    skillCourses,
    expandedReasons,
    onToggleSkill,
    onToggleReason,
  }) => (
    <Box className={styles.skillBox}>
      {/* Skill Header */}
      <Box
        className={styles.skillHeaderBox}
        onClick={() => onToggleSkill(skillKey)}
      >
        <Box className={styles.skillHeaderLeft}>
          <Chip
            label={`Priority ${skillRec.priority}`}
            size="small"
            className={styles.priorityChip}
          />
          <Typography className={styles.skillName}>
            {skillRec.skill_name}
          </Typography>
        </Box>
        <Box className={styles.skillHeaderRight}>
          <Chip
            label={`${skillCourses.length} ${skillCourses.length === 1 ? "course" : "courses"}`}
            size="small"
            className={styles.courseCountChip}
          />
          {isExpanded ? (
            <ExpandLessIcon className={styles.expandIcon} />
          ) : (
            <ExpandMoreIcon className={styles.expandIcon} />
          )}
        </Box>
      </Box>

      {/* Courses Content */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box className={styles.coursesContainer}>
          {skillCourses.map((course) => (
            <CourseCard
              key={course.course_id}
              course={course}
              isReasonExpanded={expandedReasons.has(course.course_id)}
              onToggleReason={onToggleReason}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  ),
);

SkillSection.displayName = "SkillSection";

const TrainingSearchAgent: React.FC<TrainingSearchAgentProps> = ({
  parsedContent,
}) => {
  const [viewMode, setViewMode] = useState<"skill" | "course">("skill");
  const [expandedSkills, setExpandedSkills] = useState<Set<string>>(
    () => new Set(["skill-0"]),
  );
  const [expandedReasons, setExpandedReasons] = useState<Set<string>>(
    () => new Set(),
  );

  const trainingData = useMemo<TrainingData | null>(() => {
    try {
      const finalAnswer = parsedContent?.finalAnswer;
      if (Array.isArray(finalAnswer) && finalAnswer.length > 0) {
        return JSON.parse(finalAnswer[0]);
      } else if (typeof finalAnswer === "string") {
        return JSON.parse(finalAnswer);
      } else if (parsedContent?.courses) {
        return parsedContent as TrainingData;
      }
    } catch (error) {
      console.error("Error parsing training search agent data:", error);
    }
    return null;
  }, [parsedContent]);

  const courseMap = useMemo(() => {
    if (!trainingData?.courses) return new Map();
    return new Map(
      trainingData.courses.map((course) => [course.course_id, course]),
    );
  }, [trainingData?.courses]);

  const handleSkillToggle = useCallback((skillIndex: string) => {
    setExpandedSkills((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(skillIndex)) {
        newSet.delete(skillIndex);
      } else {
        newSet.add(skillIndex);
      }
      return newSet;
    });
  }, []);

  const toggleReasonExpand = useCallback((courseId: string) => {
    setExpandedReasons((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        newSet.delete(courseId);
      } else {
        newSet.add(courseId);
      }
      return newSet;
    });
  }, []);

  const handleViewModeChange = useCallback(
    (
      _event: React.MouseEvent<HTMLElement>,
      newMode: "skill" | "course" | null,
    ) => {
      if (newMode !== null) {
        setViewMode(newMode);
      }
    },
    [],
  );

  // Sorted courses for "View by Course" mode
  // Sort by: 1) Match score (descending), 2) Duration (ascending), 3) Cost (ascending)
  const sortedCourses = useMemo(() => {
    if (!trainingData?.courses) return [];

    return [...trainingData.courses].sort((a, b) => {
      // Primary sort: Match score (higher is better)
      const scoreDiff = b.skill_match_score - a.skill_match_score;
      if (scoreDiff !== 0) return scoreDiff;

      // Secondary sort: Duration in hours (shorter is better)
      const aDuration = a.duration_hours ?? Infinity;
      const bDuration = b.duration_hours ?? Infinity;
      const durationDiff = aDuration - bDuration;
      if (durationDiff !== 0) return durationDiff;

      // Tertiary sort: Cost (cheaper is better)
      const aCost = a.cost ?? Infinity;
      const bCost = b.cost ?? Infinity;
      return aCost - bCost;
    });
  }, [trainingData?.courses]);

  if (
    !trainingData ||
    !trainingData.courses ||
    trainingData.courses.length === 0
  ) {
    return (
      <Box className={styles.errorContainer}>
        <Typography color="error">
          Unable to load training recommendations. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.trainingSearchContainer}>
      {/* Header Section */}
      <Box className={styles.headerSection}>
        <Box className={styles.headerIcon}>
          <SchoolIcon sx={{ fontSize: 32, color: "#00a888" }} />
        </Box>
        <Typography className={styles.headerTitle}>
          Your Personalized Learning Path
        </Typography>
        <Typography className={styles.headerSubtitle}>
          {trainingData.recommendations_by_skill.length} skill{" "}
          {trainingData.recommendations_by_skill.length === 1
            ? "area"
            : "areas"}{" "}
          • {trainingData.courses.length} recommended{" "}
          {trainingData.courses.length === 1 ? "course" : "courses"}
        </Typography>
      </Box>

      {/* View Mode Toggle */}
      <Box className={styles.viewModeToggleContainer}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          className={styles.viewModeToggle}
          size="small"
        >
          <ToggleButton value="skill" className={styles.toggleButton}>
            <ListIcon sx={{ fontSize: 18, mr: 0.5 }} />
            View by Skill
          </ToggleButton>
          <ToggleButton value="course" className={styles.toggleButton}>
            <ViewModuleIcon sx={{ fontSize: 18, mr: 0.5 }} />
            View by Course
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* View by Skill */}
      {viewMode === "skill" && (
        <Box className={styles.skillsSection}>
          {trainingData.recommendations_by_skill.map((skillRec, index) => {
            const skillKey = `skill-${index}`;
            const skillCourses = skillRec.course_ids
              .map((id) => courseMap.get(id))
              .filter((course): course is Course => course !== undefined);

            // Skip skills with no courses
            if (skillCourses.length === 0) return null;

            return (
              <SkillSection
                key={skillKey}
                skillRec={skillRec}
                skillKey={skillKey}
                isExpanded={expandedSkills.has(skillKey)}
                skillCourses={skillCourses}
                expandedReasons={expandedReasons}
                onToggleSkill={handleSkillToggle}
                onToggleReason={toggleReasonExpand}
              />
            );
          })}
        </Box>
      )}

      {/* View by Course */}
      {viewMode === "course" && (
        <Box className={styles.coursesListSection}>
          {sortedCourses.map((course) => (
            <CourseCard
              key={course.course_id}
              course={course}
              isReasonExpanded={expandedReasons.has(course.course_id)}
              onToggleReason={toggleReasonExpand}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default memo(TrainingSearchAgent);
